import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Calendar, MapPin, Ticket, ChevronLeft, Check, Plus, Minus, X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (d, t) => {
  if (!d) return "";
  const [y, m, day] = d.split("-").map(Number);
  const dt = new Date(y, m - 1, day);
  const str = dt.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  return t ? `${str} at ${t}` : str;
};

const fmtAgeDate = (d) => {
  if (!d) return "";
  const [y, m, day] = d.split("-").map(Number);
  return `${String(day).padStart(2,"0")}/${String(m).padStart(2,"0")}/${y}`;
};

// ── Signature Pad ────────────────────────────────────────────────────────────
function SignaturePad({ onChange }) {
  const canvasRef = useRef(null);
  const drawing   = useRef(false);
  const [hasSig, setHasSig] = useState(false);

  const getXY = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width  / rect.width;
    const scaleY = canvas.height / rect.height;
    const src = e.touches ? e.touches[0] : e;
    return { x: (src.clientX - rect.left) * scaleX, y: (src.clientY - rect.top) * scaleY };
  };

  const start = useCallback((e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const { x, y } = getXY(e, canvas);
    ctx.beginPath();
    ctx.moveTo(x, y);
    drawing.current = true;
  }, []);

  const move = useCallback((e) => {
    e.preventDefault();
    if (!drawing.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const { x, y } = getXY(e, canvas);
    ctx.lineWidth   = 2.5;
    ctx.lineCap     = "round";
    ctx.lineJoin    = "round";
    ctx.strokeStyle = "#1a1a2e";
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSig(true);
  }, []);

  const end = useCallback(() => {
    if (!drawing.current) return;
    drawing.current = false;
    if (canvasRef.current) onChange(canvasRef.current.toDataURL("image/png"));
  }, [onChange]);

  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    setHasSig(false);
    onChange(null);
  };

  return (
    <div>
      <div className="relative border-2 border-gray-200 rounded-xl overflow-hidden bg-white">
        <canvas
          ref={canvasRef}
          width={700} height={160}
          className="w-full touch-none cursor-crosshair"
          style={{ display: "block" }}
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          data-testid="signature-canvas"
        />
        {!hasSig && (
          <p className="absolute inset-0 flex items-center justify-center text-gray-300 text-sm pointer-events-none select-none">
            Sign here
          </p>
        )}
      </div>
      {hasSig && (
        <button type="button" onClick={clear}
          className="mt-1.5 flex items-center gap-1 text-xs text-[#ff3131] font-semibold hover:underline"
          data-testid="clear-signature-btn">
          <X className="w-3 h-3" /> Clear signature
        </button>
      )}
    </div>
  );
}

// ── Acknowledgement Checkbox ─────────────────────────────────────────────────
function AckBox({ checked, onChange, children }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <div
        onClick={onChange}
        className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors cursor-pointer ${
          checked ? "bg-[#ff3131] border-[#ff3131]" : "border-gray-300 hover:border-[#ff3131]/50"
        }`}
      >
        {checked && <Check className="w-3 h-3 text-white" />}
      </div>
      <span className="text-sm text-[#2D3436] leading-snug">{children}</span>
    </label>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────
export default function EventDetailPage() {
  const { eventId } = useParams();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();

  const [event, setEvent]     = useState(null);
  const [loading, setLoading] = useState(true);

  // Form state
  const [numChildren, setNumChildren] = useState(1);
  const [children, setChildren]   = useState([{ first_name: "", last_name: "", dob: "" }]);
  const [guardian, setGuardian]   = useState({ full_name: "", email: "", phone: "" });
  const [ack1, setAck1]           = useState(false);
  const [ack2, setAck2]           = useState(false);
  const [signature, setSignature] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    if (searchParams.get("cancelled") === "1") {
      toast.error("Payment cancelled — your booking was not completed");
    }
  }, [searchParams]);

  useEffect(() => {
    axios.get(`${API}/events/${eventId}`)
      .then(r => { setEvent(r.data); setLoading(false); })
      .catch(() => { toast.error("Event not found"); nav("/events"); });
  }, [eventId, nav]);

  // Sync children array length with numChildren
  useEffect(() => {
    setChildren(prev => {
      const next = [...prev];
      while (next.length < numChildren) next.push({ first_name: "", last_name: "", dob: "" });
      return next.slice(0, numChildren);
    });
  }, [numChildren]);

  const updateChild = (idx, field, val) =>
    setChildren(prev => prev.map((c, i) => i === idx ? { ...c, [field]: val } : c));

  const validate = () => {
    for (let i = 0; i < children.length; i++) {
      const c = children[i];
      if (!c.first_name.trim()) { toast.error(`Child ${i + 1}: first name is required`); return false; }
      if (!c.last_name.trim())  { toast.error(`Child ${i + 1}: last name is required`);  return false; }
      if (!c.dob)               { toast.error(`Child ${i + 1}: date of birth is required`); return false; }
    }
    if (!guardian.full_name.trim()) { toast.error("Guardian full name is required");  return false; }
    if (!guardian.email.trim())     { toast.error("Guardian email is required");       return false; }
    if (!guardian.phone.trim())     { toast.error("Guardian phone is required");       return false; }
    if (!ack1) { toast.error("Please confirm you have read acknowledgement 1"); return false; }
    if (!ack2) { toast.error("Please confirm you have read acknowledgement 2"); return false; }
    if (!signature) { toast.error("Please add your signature to complete the booking"); return false; }
    return true;
  };

  const submit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/events/${eventId}/book`, {
        responses: {
          children,
          guardian_full_name: guardian.full_name,
          guardian_email:     guardian.email,
          guardian_phone:     guardian.phone,
          ack_supervise:      "true",
          ack_parking:        "true",
          signature,
        },
        attendee_count: numChildren,
        origin_url: window.location.origin,
      });
      if (res.data.requires_payment && res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        setConfirmation(res.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Booking failed — please try again");
    }
    setSubmitting(false);
  };

  if (loading) return (
    <div className="min-h-screen bg-[#FFFBF5] flex items-center justify-center">
      <p className="text-[#636E72]">Loading…</p>
    </div>
  );

  // ── Confirmation (free events) ──────────────────────────────────────────────
  if (confirmation) return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <Header />
      <div className="max-w-lg mx-auto px-4 py-20 text-center">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <Check className="w-10 h-10 text-green-600" />
        </div>
        <h1 className="text-3xl font-bold text-[#2D3436] mb-2" style={{ fontFamily: "Fredoka, cursive" }}>Booking Confirmed!</h1>
        <p className="text-[#636E72] mb-6">You're booked in for <strong className="text-[#2D3436]">{confirmation.event_title}</strong></p>
        <div className="bg-white rounded-2xl p-6 shadow-sm mb-6">
          <p className="text-sm text-[#636E72] mb-1">Booking Reference</p>
          <p className="text-3xl font-bold text-[#ff3131] font-mono tracking-widest" style={{ fontFamily: "Fredoka, cursive" }}>{confirmation.booking_ref}</p>
        </div>
        <p className="text-sm text-[#636E72] mb-6">Please keep your booking reference safe. We look forward to seeing you!</p>
        <button onClick={() => nav("/events")}
          className="bg-[#ff3131] hover:bg-[#e62e2e] text-white px-8 py-3 rounded-full font-bold transition-colors"
          style={{ fontFamily: "Fredoka, cursive" }}>
          Back to Events
        </button>
      </div>
      <Footer />
    </div>
  );

  const soldOut = event.tickets_remaining === 0;

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <Header />

      <div className="max-w-3xl mx-auto px-4 py-8">
        <button onClick={() => nav("/events")}
          className="flex items-center gap-1 text-[#636E72] hover:text-[#2D3436] mb-6 text-sm font-semibold transition-colors">
          <ChevronLeft className="w-4 h-4" /> All events
        </button>

        {/* Event hero */}
        {event.image_data && (
          <img src={event.image_data} alt={event.title}
            className="w-full h-64 md:h-80 object-cover rounded-2xl mb-6 shadow-sm" />
        )}

        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="md:col-span-2">
            <h1 className="text-3xl md:text-4xl font-bold text-[#2D3436] mb-3 leading-tight" style={{ fontFamily: "Fredoka, cursive" }}>
              {event.title}
            </h1>
            {event.description && (
              <p className="text-[#636E72] text-sm leading-relaxed whitespace-pre-line">{event.description}</p>
            )}
          </div>

          {/* Info card */}
          <div className="bg-white rounded-2xl p-5 shadow-sm h-fit space-y-3">
            <div className="flex items-start gap-2.5">
              <Calendar className="w-4 h-4 text-[#ff3131] flex-shrink-0 mt-0.5" />
              <p className="text-sm text-[#2D3436] leading-snug">{fmtDate(event.event_date, event.event_time)}</p>
            </div>
            {event.location && (
              <div className="flex items-start gap-2.5">
                <MapPin className="w-4 h-4 text-[#ff3131] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-[#2D3436]">{event.location}</p>
              </div>
            )}
            <div className="flex items-center gap-2.5">
              <Ticket className="w-4 h-4 text-[#ff3131] flex-shrink-0" />
              <p className="text-sm text-[#2D3436]">
                {soldOut
                  ? <span className="font-bold text-red-500">Sold out</span>
                  : <span className="text-green-600 font-semibold">Spaces available</span>
                }
              </p>
            </div>
            {(event.min_age_date || event.max_age_date) && (
              <div className="pt-2 border-t border-gray-100">
                <p className="text-xs font-semibold text-[#636E72] mb-0.5">Age requirements</p>
                {event.min_age_date && <p className="text-xs text-[#2D3436]">Born on or before {fmtAgeDate(event.min_age_date)}</p>}
                {event.max_age_date && <p className="text-xs text-[#2D3436]">Born on or after {fmtAgeDate(event.max_age_date)}</p>}
              </div>
            )}
            <div className="pt-2 border-t border-gray-100">
              <p className="text-2xl font-bold text-[#ff3131]" style={{ fontFamily: "Fredoka, cursive" }}>
                {event.ticket_price > 0 ? `£${event.ticket_price.toFixed(2)} per ticket` : "Free"}
              </p>
            </div>
          </div>
        </div>

        {/* Sold out */}
        {soldOut && (
          <div className="bg-white rounded-2xl p-8 text-center shadow-sm">
            <p className="text-xl font-bold text-[#2D3436]" style={{ fontFamily: "Fredoka, cursive" }}>This event is sold out</p>
            <p className="text-sm text-[#636E72] mt-2">Check back for more upcoming events.</p>
          </div>
        )}

        {/* Booking form */}
        {!soldOut && (
          <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm space-y-8">
            <div>
              <h2 className="text-2xl font-bold text-[#2D3436]" style={{ fontFamily: "Fredoka, cursive" }}>Book Your Place</h2>
              <p className="text-sm text-[#636E72] mt-1">Fill in the details below to secure your spot.</p>
            </div>

            {/* Number of children + price */}
            <div>
              <label className="text-sm font-bold text-[#2D3436] mb-3 block">Number of children</label>
              <div className="flex items-center gap-4">
                <button type="button"
                  onClick={() => setNumChildren(n => Math.max(1, n - 1))}
                  className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
                  data-testid="dec-children-btn">
                  <Minus className="w-4 h-4 text-[#2D3436]" />
                </button>
                <span className="text-2xl font-bold text-[#2D3436] w-8 text-center" data-testid="children-count">{numChildren}</span>
                <button type="button"
                  onClick={() => setNumChildren(n => Math.min(event.total_tickets - (event.tickets_sold || 0), n + 1))}
                  className="w-10 h-10 rounded-full bg-[#ff3131] flex items-center justify-center hover:bg-[#e62e2e] transition-colors"
                  data-testid="inc-children-btn">
                  <Plus className="w-4 h-4 text-white" />
                </button>
                {event.ticket_price > 0 && (
                  <span className="text-sm text-[#636E72] ml-1">
                    × £{event.ticket_price.toFixed(2)} = <strong className="text-[#2D3436]">£{(numChildren * event.ticket_price).toFixed(2)}</strong>
                  </span>
                )}
              </div>
            </div>

            {/* Per-child details */}
            <div className="space-y-6">
              <h3 className="text-base font-bold text-[#2D3436] border-b border-gray-100 pb-2">Booking Details</h3>
              {children.map((child, i) => (
                <div key={i} className="space-y-3">
                  <p className="text-sm font-semibold text-[#ff3131]">
                    {numChildren > 1 ? `Child ${i + 1}` : "Child's Details"}
                  </p>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold text-[#636E72] mb-1 block">
                        First name <span className="text-[#ff3131]">*</span>
                      </label>
                      <input type="text" value={child.first_name}
                        onChange={e => updateChild(i, "first_name", e.target.value)}
                        placeholder="First name"
                        className="w-full p-3 border-2 border-gray-100 rounded-xl text-sm focus:border-[#ff3131] outline-none"
                        data-testid={`child-${i}-first-name`} />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-[#636E72] mb-1 block">
                        Last name <span className="text-[#ff3131]">*</span>
                      </label>
                      <input type="text" value={child.last_name}
                        onChange={e => updateChild(i, "last_name", e.target.value)}
                        placeholder="Last name"
                        className="w-full p-3 border-2 border-gray-100 rounded-xl text-sm focus:border-[#ff3131] outline-none"
                        data-testid={`child-${i}-last-name`} />
                    </div>
                  </div>
                  <div className="md:w-1/2">
                    <label className="text-xs font-semibold text-[#636E72] mb-1 block">
                      Date of birth <span className="text-[#ff3131]">*</span>
                    </label>
                    <input type="date" value={child.dob}
                      onChange={e => updateChild(i, "dob", e.target.value)}
                      className="w-full p-3 border-2 border-gray-100 rounded-xl text-sm focus:border-[#ff3131] outline-none"
                      data-testid={`child-${i}-dob`} />
                  </div>
                </div>
              ))}
            </div>

            {/* Parent / Guardian */}
            <div className="space-y-4">
              <h3 className="text-base font-bold text-[#2D3436] border-b border-gray-100 pb-2">Parent / Guardian Details</h3>
              <div>
                <label className="text-xs font-semibold text-[#636E72] mb-1 block">
                  Full name <span className="text-[#ff3131]">*</span>
                </label>
                <input type="text" value={guardian.full_name}
                  onChange={e => setGuardian(g => ({ ...g, full_name: e.target.value }))}
                  placeholder="Full name"
                  className="w-full p-3 border-2 border-gray-100 rounded-xl text-sm focus:border-[#ff3131] outline-none"
                  data-testid="guardian-name-input" />
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-[#636E72] mb-1 block">
                    Email address <span className="text-[#ff3131]">*</span>
                  </label>
                  <input type="email" value={guardian.email}
                    onChange={e => setGuardian(g => ({ ...g, email: e.target.value }))}
                    placeholder="your@email.com"
                    className="w-full p-3 border-2 border-gray-100 rounded-xl text-sm focus:border-[#ff3131] outline-none"
                    data-testid="guardian-email-input" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#636E72] mb-1 block">
                    Phone number <span className="text-[#ff3131]">*</span>
                  </label>
                  <input type="tel" value={guardian.phone}
                    onChange={e => setGuardian(g => ({ ...g, phone: e.target.value }))}
                    placeholder="07700 900000"
                    className="w-full p-3 border-2 border-gray-100 rounded-xl text-sm focus:border-[#ff3131] outline-none"
                    data-testid="guardian-phone-input" />
                </div>
              </div>
            </div>

            {/* Acknowledgements */}
            <div className="space-y-4">
              <h3 className="text-base font-bold text-[#2D3436] border-b border-gray-100 pb-2">Required Acknowledgements</h3>
              <div className="space-y-4">
                <AckBox checked={ack1} onChange={() => setAck1(v => !v)} data-testid="ack1-checkbox">
                  I understand that a parent or guardian must attend the session with every child on this booking and supervise them at all times.
                </AckBox>
                <AckBox checked={ack2} onChange={() => setAck2(v => !v)} data-testid="ack2-checkbox">
                  I understand that I must follow the parking signs and restrictions at Hamble Sports Complex and enter my vehicle registration if required. If someone else brings the children, I am responsible for passing this information on to them.
                </AckBox>
              </div>
            </div>

            {/* Signature */}
            <div>
              <h3 className="text-base font-bold text-[#2D3436] border-b border-gray-100 pb-2 mb-4">Signature</h3>
              <p className="text-sm text-[#636E72] mb-3">
                By signing below you confirm all the information provided is accurate and you agree to the acknowledgements above.
              </p>
              <SignaturePad onChange={setSignature} />
            </div>

            {/* Submit */}
            <button onClick={submit} disabled={submitting}
              className="w-full bg-[#ff3131] hover:bg-[#e62e2e] text-white py-4 rounded-full font-bold text-lg disabled:opacity-60 transition-colors"
              style={{ fontFamily: "Fredoka, cursive" }}
              data-testid="book-event-btn">
              {submitting
                ? "Processing…"
                : event.ticket_price > 0
                  ? `Continue to Payment — £${(numChildren * event.ticket_price).toFixed(2)}`
                  : "Confirm Booking"}
            </button>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
